'use client';
import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';

import { cn } from '@cineamo/ui/src/helpers/cn';
import { tailwindColors } from '@cineamo/ui/src/helpers/tailwind-config-helper';
import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { IconForthTriangle8 } from '@cineamo/ui/src/icons/ic-forthTriangle';
import { IconFullscreen16 } from '@cineamo/ui/src/icons/ic-fullscreen';
import { IconMinimize16 } from '@cineamo/ui/src/icons/ic-minimize';
import { IconSparkle16 } from '@cineamo/ui/src/icons/ic-sparkle';
import ProgressBarAnimation from '@cineamo/ui/src/loading/ProgressBarAnimation';
import { DrawerOptions, HEX_COLOR } from '@cineamo/ui/src/seat-map/Drawer';
import { seatColors } from '@cineamo/ui/src/seat-map/exampleData';
import SeatMap from '@cineamo/ui/src/seat-map/SeatMap';
import { Seat, CinemaRoom, RoomObject, SeatCategory } from '@cineamo/ui/src/seat-map/typedefs';
import SeatMap3DLazy from '@cineamo/ui/src/seat-map-3d/SeatMap3DLazy';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';
import { useCartProvider } from '@cineamo/composables/src/providers/cartProvider';
import { recommendBestSeats } from '@cineamo/composables/src/ticket-booking/best-seats';
import SeatMapViewToggle from '@cineamo/composables/src/ticket-booking/SeatMapViewToggle';
import { useSeatMapViewMode } from '@cineamo/composables/src/ticket-booking/useSeatMapViewMode';

/** A control laid over the map: translucent dark, readable on any room and any website theme. */
const MAP_CONTROL =
    'pointer-events-auto flex items-center justify-center bg-black/50 text-white backdrop-blur-[6px] outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-color)]';

/** The map's height on small screens: the space between its top and the fixed checkout footer, set by `useFitAboveFooter`. */
const MOBILE_MAP_HEIGHT = 'h-[var(--seat-map-height,60svh)]';
/** Below this the map gets too small to tap a seat; a shorter screen scrolls the rest out from under the footer. */
const MIN_MOBILE_MAP_HEIGHT = 320;

/**
 * On small screens the checkout footer (legend, seats, total) is fixed over the bottom of the page. A map of a fixed
 * share of the viewport ran under it on most phones, and as the map takes the touch gestures the hidden rows could
 * not be scrolled into view. So it is sized to what is left between its top and the footer, measured, since both
 * the details above it and the footer change height with the film title, the language and the screen width.
 */
function useFitAboveFooter(ref: React.RefObject<HTMLDivElement | null>, isMapShown: boolean) {
    useLayoutEffect(() => {
        const container = ref.current;
        if (!container || !isMapShown) return;
        const footer = document.querySelector<HTMLElement>('[data-booking-footer]');
        const fit = () => {
            const top = container.getBoundingClientRect().top + window.scrollY;
            // clientHeight, not innerHeight: on iOS it is the viewport with the toolbars shown, so it does not
            // change, and redraw the map, while the page scrolls.
            const visible = document.documentElement.clientHeight - (footer?.offsetHeight ?? 0) - 8;
            // The floor never exceeds the visible space: a map filling it (landscape) takes every swipe, a trap.
            const height = Math.max(Math.min(MIN_MOBILE_MAP_HEIGHT, visible), visible - top);
            container.style.setProperty('--seat-map-height', `${height}px`);
        };
        fit();
        // The body grows when the details above the map load, the footer when its legend appears. Deferred a frame,
        // as fitting resizes the body itself, which within the callback is a ResizeObserver loop error.
        let frame = 0;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(fit);
        });
        observer.observe(document.body);
        if (footer) observer.observe(footer);
        window.addEventListener('resize', fit);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            window.removeEventListener('resize', fit);
        };
    }, [ref, isMapShown]);
}

const SeatSelection: React.FC<{
    /** Absent while the room query is pending — the seat map cannot be drawn without it. */
    room?: CinemaRoom;
    seats: Seat[];
    roomObjects?: RoomObject[];
    isLoading?: boolean;
    seatCategories: SeatCategory[];
    selectedSeatColor: HEX_COLOR;
    isDisabled?: boolean;
    className?: string;
    screenImageUrl?: string;
}> = ({
    room,
    seats: seatsProp,
    roomObjects,
    seatCategories,
    selectedSeatColor,
    isDisabled,
    className,
    isLoading,
    screenImageUrl
}) => {
    const t = useTranslations('tickets');
    // used to force redraw of the seat map
    const [seatMapDrawVersion, setSeatMapDrawVersion] = useState(0);
    const { track } = useAnalytics();

    const seatColorsByCategory = useMemo<DrawerOptions['seatColorsByCategory']>(() => {
        const obj: Record<string, HEX_COLOR> = {};
        if (seatCategories) {
            seatCategories.forEach((category, index) => {
                obj[category.name ?? ''] = (category.color ?? seatColors[index % seatColors.length]) as HEX_COLOR;
            });
        }
        return obj;
    }, [seatCategories]);

    const { isLoaded, selectSeat, unselectSeat, items, selectedSeats, seatLocks, errors } = useCartProvider();

    const [showSelectSeatsCta, setShowSelectSeatsCta] = useState<boolean | null>(null);

    useEffect(() => {
        if (!isLoaded) {
            return;
        }
        if (showSelectSeatsCta === false) {
            return;
        }
        if (selectedSeats.length === 0 && showSelectSeatsCta === null) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: show CTA once after cart loads when no seats selected
            setShowSelectSeatsCta(true);
        } else if (selectedSeats.length !== 0) {
            setShowSelectSeatsCta(false);
        }
    }, [isLoaded, selectedSeats.length, showSelectSeatsCta]);

    useEffect(() => {
        if (!showSelectSeatsCta) return;
        const timer = setTimeout(() => setShowSelectSeatsCta(false), 5000);
        return () => clearTimeout(timer);
    }, [showSelectSeatsCta]);

    const onSeatClick = useCallback(
        async (seat: Seat) => {
            seat.isProcessing = true;
            if (isLoaded) {
                // sync user selection with Cart items
                if (seat.isSelected) {
                    selectSeat(seat);
                } else {
                    unselectSeat(seat.id);
                    track('Seat Unselected', {
                        seatId: seat.id,
                        seatName: seat.positionName,
                        seatRow: seat.rowName
                    });
                }
            }
            seat.isProcessing = false;
            setSeatMapDrawVersion((prev) => prev + 1);
        },
        [selectSeat, unselectSeat, isLoaded, track]
    );

    const seats = useMemo(() => {
        return seatsProp?.map((seat) => {
            const result = { ...seat };
            const seatLock = seatLocks?.find((seatLock) => seatLock._embedded?.seat?.id === seat.id);
            const cartItem = items.find((item) => item._embedded?.product?._embedded?.seat?.id === seat.id);
            const isSelected = selectedSeats.some((s) => s.id === seat.id);
            const isError = errors.some((error) => error.seatId === seat.id);

            if (seatLock && !cartItem) {
                result.isLocked = true;
                result.isSelected = false;
            }

            if (isSelected) {
                result.isSelected = true;
                result.isLocked = false;
            }

            result.isError = isSelected && isError;
            return result;
        });
    }, [seatsProp, seatLocks, items, selectedSeats, errors]);

    const { mode, setMode, canUse3D, disable3D, assignedMode } = useSeatMapViewMode();
    // The enlarged map covers the whole viewport: a fixed overlay rendered into the body (a website's own
    // layout may contain fixed elements), rather than the Fullscreen API, which iOS Safari offers only for video.
    const [isExpanded, setIsExpanded] = useState(false);
    useEffect(() => {
        if (!isExpanded) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsExpanded(false);
        };
        const { overflow } = document.body.style;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = overflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isExpanded]);

    const onViewModeChange = useCallback(
        (next: typeof mode) => {
            setMode(next);
            // With only two views, `isSwitchAway` says everything the experiment needs: true is
            // "left the view we assigned", false is "came back to it". Sent as its own property
            // rather than left to be derived in PostHog, whose metric filters compare a property to
            // a literal and cannot compare two properties to each other. Both are absent (and so
            // dropped by the tracker) for anyone not enrolled, where neither has a meaning.
            track('Seat Map View Mode Switched', {
                mode: next,
                assignedMode: assignedMode ?? undefined,
                isSwitchAway: assignedMode ? next !== assignedMode : undefined
            });
        },
        [setMode, track, assignedMode]
    );

    const on3DUnavailable = useCallback(() => {
        disable3D();
        showToast({ toastType: toast.info, messageTitle: t('seat-map-3d-unavailable') });
    }, [disable3D, t]);

    const onBestSeats = useCallback(() => {
        // Picks made before the cart has loaded would be overwritten by the cart sync.
        if (!isLoaded) return;
        const picks = recommendBestSeats(seats ?? [], selectedSeats.length || 2);
        if (picks.length === 0) {
            showToast({ toastType: toast.info, messageTitle: t('best-seats-none') });
            return;
        }
        selectedSeats.forEach((s) => unselectSeat(s.id));
        picks.forEach((s) => selectSeat({ ...s, isSelected: true }));
        track('Best Seats Picked', { count: picks.length });
        setSeatMapDrawVersion((prev) => prev + 1);
    }, [isLoaded, seats, selectedSeats, selectSeat, unselectSeat, track, t]);

    const seatMap3DColors = useMemo(
        () => ({
            selectedSeatColor,
            errorColor: tailwindColors.red.DEFAULT,
            seatColorsByCategory: seatColorsByCategory as Record<string, string>,
            fallbackColor: seatColors[0]
        }),
        [selectedSeatColor, seatColorsByCategory]
    );

    const containerRef = useRef<HTMLDivElement>(null);
    useFitAboveFooter(containerRef, !isLoading && !!room);

    const trackedEvents = useRef<Record<string, boolean>>({
        Drag: false,
        Pinch: false,
        'Mouse Wheel': false,
        Orbit: false,
        Zoom: false,
        'View From Seat': false
    });

    const mapView = !room ? null : (
        <div
            className={cn(
                'relative flex flex-col lg:h-full',
                isExpanded && 'fixed inset-0 z-[1000] h-[100dvh] lg:h-[100dvh] bg-[var(--background-color)]'
            )}>
            {/* Controls over the map, as in the booking design: best seats on the left, the view
                toggle and the enlarge button on the right. */}
            {!isDisabled && (
                <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-start justify-between gap-8 p-8">
                    <button
                        type="button"
                        onClick={onBestSeats}
                        disabled={!isLoaded}
                        className={cn(
                            MAP_CONTROL,
                            'gap-8 rounded-[6px] p-6 text-body-bold-12 disabled:cursor-not-allowed disabled:opacity-50'
                        )}>
                        <IconSparkle16 fill="currentColor" />
                        {t('best-seats')}
                    </button>
                    <div className="flex flex-col items-end gap-8">
                        {canUse3D && (
                            <SeatMapViewToggle
                                className="pointer-events-auto"
                                mode={mode}
                                onChange={onViewModeChange}
                            />
                        )}
                        <button
                            type="button"
                            onClick={() => setIsExpanded((value) => !value)}
                            aria-label={t(isExpanded ? 'seat-map-collapse' : 'seat-map-expand')}
                            aria-pressed={isExpanded}
                            className={cn(MAP_CONTROL, 'rounded-4 p-8')}>
                            {isExpanded ? (
                                <IconMinimize16 fill="currentColor" />
                            ) : (
                                <IconFullscreen16 fill="currentColor" />
                            )}
                        </button>
                    </div>
                </div>
            )}
            <div className={classnames('relative lg:grow lg:min-h-0', isExpanded && 'grow min-h-0')}>
                {!isLoaded && (
                    <div className="absolute top-0 left-0 w-full h-full backdrop-blur-sm flex items-center justify-center">
                        <div className="text-body-14">{t('loading')}</div>
                    </div>
                )}
                {showSelectSeatsCta && mode === '2D' && (
                    <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none">
                        <div className="relative bg-white text-darkBlue rounded-[6px] py-8 px-12 text-body-14">
                            {t('select-seats-cta')}
                            <div className="absolute top-full right-1/2 -translate-x-1/2">
                                <IconForthTriangle8
                                    fill={tailwindColors.white.DEFAULT}
                                    className="w-12 h-12 rotate-90 -translate-y-[2px]"
                                />
                            </div>
                        </div>
                    </div>
                )}
                {mode === '3D' && canUse3D ? (
                    <SeatMap3DLazy
                        className={cn(MOBILE_MAP_HEIGHT, 'lg:h-full', isExpanded && 'h-full')}
                        data={{ room, seats, roomObjects }}
                        colors={seatMap3DColors}
                        screenImageUrl={screenImageUrl}
                        isDisabled={isDisabled}
                        onSeatClick={isDisabled ? undefined : onSeatClick}
                        onUnavailable={on3DUnavailable}
                        trackEvent={(e) => {
                            if (trackedEvents.current[e]) return;
                            trackedEvents.current[e] = true;
                            track('Seat Map Used', {
                                event: e,
                                view: '3D',
                                deviceWidth: window.innerWidth,
                                deviceHeight: window.innerHeight
                            });
                        }}
                        labels={{
                            seatUnavailable: t('seat-unavailable'),
                            wheelchairAccessible: t('seat-wheelchair-accessible'),
                            viewFromSeat: t('view-from-seat'),
                            backToOverview: t('back-to-overview'),
                            lightsDown: t('lights-down'),
                            lightsUp: t('lights-up')
                        }}
                    />
                ) : isDisabled ? (
                    <SeatMap
                        className="lg:h-full"
                        data={{ room, seats, roomObjects }}
                        seatColorsByCategory={seatColorsByCategory}
                        isDisabled
                    />
                ) : (
                    <SeatMap
                        // The top padding keeps the screen clear of the controls laid over the map: on small screens
                        // the screen spans the width, so it goes below the enlarge button too. On mobile the map gets the
                        // 3D view's bounded height: sized to the room, a deep room's canvas (touch-none) outgrew the
                        // viewport and swallowed every swipe, so the page could not be scrolled to its end.
                        className={cn('pt-[88px] lg:pt-48 lg:h-full', MOBILE_MAP_HEIGHT, isExpanded && 'h-full')}
                        isDisabled={false}
                        onMouseEnter={() => setShowSelectSeatsCta(false)}
                        data={{ room, seats, roomObjects }}
                        seatColorsByCategory={seatColorsByCategory}
                        onSeatClick={onSeatClick}
                        drawVersion={seatMapDrawVersion}
                        trackEvent={(e: string) => {
                            if (trackedEvents.current[e]) {
                                return;
                            }
                            trackedEvents.current[e] = true;
                            track('Seat Map Used', {
                                event: e,
                                deviceWidth: window.innerWidth,
                                deviceHeight: window.innerHeight
                            });
                        }}
                        selectedSeatColor={selectedSeatColor}
                    />
                )}
            </div>
        </div>
    );

    return (
        <div
            ref={containerRef}
            className={classnames(className, 'lg:pb-0 lg:absolute lg:inset-0')}>
            {isLoading || !room ? (
                <div className="relative">
                    <div className="min-h-[50vh] flex flex-col items-center justify-center space-y-8">
                        <p className="font-body-bold-14 opacity-50 mb-4">{t('loading-seat-map')}</p>
                        <ProgressBarAnimation className="w-[200px] h-[24px]" />
                    </div>
                </div>
            ) : isExpanded ? (
                createPortal(mapView, document.body)
            ) : (
                mapView
            )}
        </div>
    );
};

export default SeatSelection;
